import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOpportunityWizard } from './OpportunityWizardContext';
import OpportunityWizardLayout from './OpportunityWizardLayout';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const opportunityTypes = {
  new_business: 'Novo Negócio',
  upgrade: 'Upgrade',
  renewal: 'Renovação',
  expansion: 'Expansão',
  cross_sell: 'Cross-Sell'
};

const opportunityStages = {
  qualification: 'Qualificação',
  presentation: 'Apresentação',
  proposal: 'Proposta',
  negotiation: 'Negociação',
  closed_won: 'Fechado Ganho',
  closed_lost: 'Fechado Perdido'
};

const Step6Summary = () => {
  const navigate = useNavigate();
  const { state, resetWizard } = useOpportunityWizard();
  const [isCreating, setIsCreating] = useState(false);

  // Verificar se temos as informações necessárias
  if (!state.selectedCustomer) {
    navigate('../step1', { relative: 'path' });
    return null;
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: state.currencyCode,
      minimumFractionDigits: 2
    }).format(value);
  };

  const getTotalValue = () => {
    return state.selectedPlans.reduce((total, plan) => {
      return total + (plan.quantity * plan.unit_price);
    }, 0);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: ptBR });
    } catch (error) {
      return dateString;
    }
  };

  const handleNextStep = () => {
    navigate('../step7', { relative: 'path' });
  };

  return (
    <OpportunityWizardLayout
      currentStep={6}
      totalSteps={7}
      title="Resumo da Oportunidade"
      description="Revise as informações antes de criar a oportunidade"
      onPrevious={() => navigate('../step5', { relative: 'path' })}
      nextButtonText="Continuar para Proposta"
      onNext={handleNextStep}
      isNextDisabled={isCreating}
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-extrabold">Cliente: {state.selectedCustomer?.name}</h2>
          </div>
        </div>

        {/* Cliente */}
        <Card>
          <CardHeader>
            <CardTitle>Cliente</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <div className="font-extrabold">{state.selectedCustomer.name}</div>
              {state.selectedCustomer?.legal_name && (
                <div className="text-sm text-muted-foreground mt-1">
                  {state.selectedCustomer.legal_name}
                </div>
              )}
              {state.selectedCustomer?.business_identification_number && (
                <div className="text-sm text-muted-foreground">
                  CNPJ: {state.selectedCustomer.business_identification_number}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contatos */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Contatos</CardTitle>
          </CardHeader>
          <CardContent>
            {state.selectedContacts.length === 0 ? (
              <div className="text-muted-foreground">Nenhum contato selecionado</div>
            ) : (
              <div className="grid gap-4">
                {state.selectedContacts.map((contact, index) => (
                  <div key={index} className="border-b pb-3 last:border-b-0 last:pb-0">
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold">
                        {contact.contact.first_name} {contact.contact.last_name}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                        {contact.contact_type === 'technical' && 'Técnico'}
                        {contact.contact_type === 'administrative' && 'Administrativo'}
                        {contact.contact_type === 'billing' && 'Financeiro'}
                        {contact.contact_type === 'pedagogical' && 'Pedagógico'}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {contact.contact.email}
                      {contact.contact.phone && ` • ${contact.contact.phone}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detalhes da Oportunidade */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Detalhes da Oportunidade</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-extrabold text-muted-foreground">Data de Início:</div>
                <div>{formatDate(state.startDate)}</div>
              </div>
              <div>
                <div className="text-sm font-extrabold text-muted-foreground">Moeda:</div>
                <div>{state.currencyCode}</div>
              </div>
              <div>
                <div className="text-sm font-extrabold text-muted-foreground">Tipo de Oportunidade:</div>
                <div>{opportunityTypes[state.opportunityType as keyof typeof opportunityTypes] || state.opportunityType}</div>
              </div>
              <div>
                <div className="text-sm font-extrabold text-muted-foreground">Estágio:</div>
                <div>{opportunityStages[state.stage as keyof typeof opportunityStages] || state.stage}</div>
              </div>
              <div>
                <div className="text-sm font-extrabold text-muted-foreground">Probabilidade de Fechamento:</div>
                <div>{state.closeProbability}%</div>
              </div>
              <div>
                <div className="text-sm font-extrabold text-muted-foreground">Valor da Oportunidade:</div>
                <div className="font-extrabold">{formatCurrency(getTotalValue())}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Produtos */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Produtos</CardTitle>
          </CardHeader>
          <CardContent>
            {state.selectedPlans.length === 0 ? (
              <div className="text-muted-foreground">Nenhum produto adicionado</div>
            ) : (
              <div className="space-y-4">
                {state.selectedPlans.map((plan, index) => (
                  <div key={index} className="border-b pb-4 last:border-b-0 last:pb-0">
                    <div className="font-extrabold">{plan.subscription_plan.name}</div>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 mt-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Quantidade: </span>
                        {plan.quantity}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Preço unitário: </span>
                        {formatCurrency(plan.unit_price)}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total: </span>
                        <span className="font-extrabold">{formatCurrency(plan.unit_price * plan.quantity)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Período: </span>
                        {plan.billing_period} {plan.billing_period_unit === 'month' ? 'meses' : 
                          plan.billing_period_unit === 'year' ? 'anos' : 
                          plan.billing_period_unit === 'week' ? 'semanas' : 'dias'}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Data de início: </span>
                        {formatDate(plan.current_term_start)}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Data de término: </span>
                        {formatDate(plan.current_term_end)}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Renovação automática: </span>
                        {plan.auto_renew ? 'Sim' : 'Não'}
                      </div>
                    </div>
                  </div>
                ))}

                <Separator className="my-4" />
                
                <div className="flex justify-between items-center">
                  <div className="text-muted-foreground">Total:</div>
                  <div className="text-xl font-extrabold">{formatCurrency(getTotalValue())}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {isCreating && (
          <div className="flex justify-center items-center py-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Criando oportunidade...</span>
          </div>
        )}
      </div>
    </OpportunityWizardLayout>
  );
};

export default Step6Summary; 